/*
 * Copyright (c) 2016-2023 VMware, Inc. All Rights Reserved.
 * This software is released under MIT license.
 * The full license information can be found in LICENSE in the root directory of this project.
 */
import { querySelectorRoots } from '../utils/dom.js';
import { onChildListMutation, onFirstInteraction } from '../utils/events.js';
import { createId } from '../utils/identity.js';
import { KeyNavigationListController } from './key-navigation-list.controller.js';
let dragSrcEl = null;
/**
 * Provides support for HTML5 native drag and drop to a component
 */
export function draggableList(config) {
    return (target) => target.addInitializer((instance) => new DraggableListController(instance, config));
}
export class DraggableListController {
    constructor(host, config) {
        this.host = host;
        this.id = `__${createId()}`;
        this.host.addController(this);
        this.config = {
            layout: 'both',
            item: '',
            dropZone: '',
            manageFocus: true,
            manageTabindex: false,
            ...config,
        };
    }
    get items() {
        return querySelectorRoots(this.host, `${this.config.item}[draggable="true"]`);
    }
    get dropZones() {
        return querySelectorRoots(this.host, `${this.config.dropZone}[draggable="false"]`);
    }
    async hostConnected() {
        await this.host.updateComplete;
        onFirstInteraction(this.host).then(() => {
            this.addDragEventListeners(this.items);
            this.initializeKeyListController();
            this.host.addEventListener('click', async (e) => this.clickItem(e));
            this.host.addEventListener('cdsKeyChange', (e) => this.focusItem(e));
            this.host.shadowRoot?.addEventListener('click', async (e) => this.clickItem(e));
            this.host.shadowRoot?.addEventListener('cdsKeyChange', (e) => this.focusItem(e));
            this.observer = onChildListMutation(this.host, mutation => {
                if (mutation) {
                    const items = Array.from(mutation.addedNodes).filter(i => i.draggable);
                    if (items.length) {
                        this.addDragEventListeners(items);
                    }
                }
            });
        });
    }
    hostDisconnected() {
        this.observer?.disconnect();
    }
    initializeKeyListController() {
        Object.defineProperty(this.host, this.id, { get: () => this.items.map(i => i.querySelector('[cds-draggable]')) });
        new KeyNavigationListController(this.host, {
            layout: this.config.layout,
            keyListItems: this.id,
            manageFocus: this.config.manageFocus,
            manageTabindex: this.config.manageTabindex,
        });
    }
    clickItem(e) {
        const handle = Array.from(e.composedPath()).find((e) => e.getAttribute && e.getAttribute('cds-draggable') === 'handle');
        const from = e.composedPath()[0].closest('[draggable]');
        if (handle && from) {
            if (handle.ariaPressed === 'true') {
                from.setAttribute('cds-draggable', 'active');
                dispatchDraggableChange(e.currentTarget, from, null, 'grabbed', 'touch');
            }
            else if (handle.ariaPressed === 'false') {
                from.removeAttribute('cds-draggable');
                dispatchDraggableChange(e.currentTarget, from, null, 'dropped', 'touch');
            }
        }
    }
    focusItem(e) {
        if (e.detail.keyListItems === this.id &&
            e.detail.previousItem?.closest('[draggable]').getAttribute('cds-draggable') === 'active') {
            const from = e.detail.previousItem?.closest('[draggable]');
            const target = e.detail.activeItem.closest('[draggable]');
            if (e.detail.activeItem.getAttribute('cds-draggable') === 'handle' && from !== target) {
                e.detail.previousItem.ariaPressed = 'false';
                e.detail.activeItem.ariaPressed = 'true';
                e.detail.previousItem.pressed = false;
                e.detail.activeItem.pressed = true;
                from.removeAttribute('cds-draggable');
                target.setAttribute('cds-draggable', 'active');
                dispatchDraggableChange(e.detail.activeItem, from, target, 'reordered', 'key');
            }
        }
    }
    addDragEventListeners(items) {
        items.filter(i => !i.cdsDraggableItem).forEach(item => addHandlers(item));
        this.dropZones
            .filter(i => !i.cdsDraggableItem)
            .forEach(elem => {
            elem.addEventListener('dragover', handleDragOver, false);
            elem.addEventListener('dragleave', handleDragLeave, false);
            elem.addEventListener('drop', handleDrop, false);
            elem.cdsDraggableItem = 'dropzone';
        });
    }
}
async function dispatchDraggableChange(host, from, target, type, interaction) {
    if (host?.updateComplete) {
        await host.updateComplete;
    }
    host.dispatchEvent(new CustomEvent('cdsDraggableChange', { detail: { from, target, type, interaction }, bubbles: true }));
}
function handleDragStart(e) {
    dragSrcEl = e.currentTarget;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setDragImage(e.currentTarget, 0, 0);
    e.currentTarget.setAttribute('cds-draggable', 'active');
    dispatchDraggableChange(e.currentTarget, e.currentTarget, null, 'grabbed', 'touch');
}
function handleDragOver(e) {
    if (e.preventDefault) {
        e.preventDefault();
    }
    e.dataTransfer.dropEffect = 'move';
    if (dragSrcEl !== e.currentTarget) {
        e.currentTarget.setAttribute('cds-draggable', 'target');
    }
    return false;
}
function handleDrop(e) {
    const from = dragSrcEl;
    const target = e.currentTarget;
    from?.removeAttribute('cds-draggable');
    target?.removeAttribute('cds-draggable');
    dispatchDraggableChange(e.currentTarget, from, target, 'reordered', 'touch');
    return false;
}
function handleDragLeave(e) {
    if (e.currentTarget.getAttribute('cds-draggable') === 'target') {
        e.currentTarget.removeAttribute('cds-draggable');
    }
}
function addHandlers(elem) {
    elem.cdsDraggableItem = 'item';
    elem.addEventListener('dragstart', handleDragStart, false);
    elem.addEventListener('dragover', handleDragOver, false);
    elem.addEventListener('drop', handleDrop, false);
    elem.addEventListener('dragleave', handleDragLeave, false);
    elem.addEventListener('dragend', (e) => e.currentTarget.removeAttribute('cds-draggable'), false);
}
